/**
 * Module dependencies.
 */

import { Logo, UserBadge, getAuthUser } from './brand';
import { SiteFooter } from './site-footer';
import { appRoutes } from 'app/routes';
import { useNavigate } from 'react-router-dom';
import React, { useMemo } from 'react';

/**
 * Export `PageShell` — shared chrome (background, nav, footer) for content pages.
 */

export function PageShell({ children }: { children: React.ReactNode }): JSX.Element {
  const navigate = useNavigate();
  const authUser = useMemo(() => getAuthUser(), []);

  return (
    <div
      className={"relative isolate flex min-h-screen flex-col overflow-x-hidden font-['Roboto'] text-[#0A0A0B]"}
      style={{ backgroundColor: '#F4F4F6' }}
    >
      {/* Decorative background */}
      <div
        aria-hidden
        className={'pointer-events-none absolute inset-0 -z-10'}
        style={{
          backgroundImage: 'radial-gradient(circle, rgba(10,10,11,0.16) 1px, transparent 1px)',
          backgroundSize: '26px 26px',
          maskImage: 'radial-gradient(90% 40% at 50% 0%, #000 30%, transparent 100%)',
          WebkitMaskImage: 'radial-gradient(90% 40% at 50% 0%, #000 30%, transparent 100%)'
        }}
      />

      <div
        aria-hidden
        className={'pointer-events-none absolute -left-40 -top-44 -z-10 h-[36rem] w-[36rem] rounded-full'}
        style={{
          backgroundImage: 'radial-gradient(circle at 40% 40%, rgba(34,165,68,0.28), transparent 70%)',
          filter: 'blur(100px)'
        }}
      />

      {/* Nav */}
      <header
        className={'sticky top-0 z-20 border-b'}
        style={{
          backdropFilter: 'blur(18px) saturate(1.6)',
          WebkitBackdropFilter: 'blur(18px) saturate(1.6)',
          backgroundColor: 'rgba(255,255,255,0.62)',
          borderColor: 'rgba(228,228,231,0.7)'
        }}
      >
        <div className={'mx-auto flex h-[72px] w-full max-w-5xl items-center justify-between px-6'}>
          <button aria-label={'Contab home'} onClick={() => navigate(appRoutes.home)} type={'button'}>
            <Logo />
          </button>

          {authUser ? (
            <UserBadge onClick={() => navigate(appRoutes.dashboard.home.base)} user={authUser} />
          ) : (
            <button
              className={
                'rounded-lg border border-neutral-200 bg-white px-4 py-2 text-sm font-medium text-neutral-800 shadow-sm transition-colors hover:bg-neutral-50'
              }
              onClick={() => navigate(appRoutes.login)}
              type={'button'}
            >
              {'Sign in'}
            </button>
          )}
        </div>
      </header>

      {children}

      <SiteFooter />
    </div>
  );
}
